import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import styles from './Card.module.scss';
import { useDrag } from 'react-dnd';
import { MyQuizEnum } from '../../../types';
import Tooltip from '@src/components/tooltip/Tooltip';
import { getMediaAbsolutePath } from '../../../services/File';
import IconMoveLesson from 'src/assets/icons/move_addition.svg?react';
import IconRemovUnit from 'src/assets/icons/remove_unit.svg?react';
import MarkCheckIcon from '../../../assets/icons/mark_check.svg?react';
import InprogressIcon from '../../../assets/icons/mark_inprogress_new.svg?react';
import useDragScrolling from "./useDragScrolling";
import { DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { Unit, UnitsByProgram } from '@src/api/graphql-codegen';
import IconMark from 'src/assets/icons/mark_completed.svg?react';
import { IconInfo } from '@src/constants';
import { Popover } from 'antd';
import CoverVideoPlayer from '@src/components/lesson-progress-modal/cover-video/VideoPlayer';
import OverviewForm from './overview/OverviewForm';
import './customStyle.css';
import { LordIcon } from '@src/components/lord-icon/lord-icon';

type Props = {
  type: string
  source?: number
  target?: string
  unit: Unit
  isDragable: boolean
  handleLeftArrowAction?: (unit: Unit) => void
  handleRightArrowAction?: (unit: Unit) => void
  handleOpenUnit?: (unit: Unit) => void
  draggableProvided?: DraggableProvided;
  snapshot?: DraggableStateSnapshot;
  studyMode?: string
  handleMovableUnit?: (movable: boolean) => void
  showLabel?: boolean
  cardHovered?: boolean
  hoveredLessonId?: string | ""
  index: number
  handleCardHovered?: (lessonId: string, hovered: boolean) => void
  handleSelectedCenterCard?: (centerCard: boolean) => void
  programs?: UnitsByProgram[];
}

function drawLine(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number) {
  ctx?.beginPath();
  ctx?.moveTo(x1, y1)
  ctx?.lineTo(x2, y2);
  ctx?.stroke();
}

function drawCorner(ctx: CanvasRenderingContext2D, cornerRadius: number, x: number, y: number, start: number, end: number) {
  ctx?.beginPath();
  ctx?.arc(x, y, cornerRadius, start, end, false);
  ctx?.stroke();
}

// draw the radius rectangle
function drawPercentRect(context: CanvasRenderingContext2D, percent: number, borderWidth: number, canvasWidth: number, canvasHeight: number) {
  // define the rectangle
  var offsetX = borderWidth / 2;
  var offsetY = borderWidth / 2;
  var cornerRadius = 30;
  var horizLineLength = canvasWidth! - (cornerRadius * Math.PI) / 2 - 2 * borderWidth - 2;
  var vertLineLength = canvasHeight! - (cornerRadius * Math.PI) / 2 - 2 * borderWidth - 2;

  // calc some lengths for use in percent complete
  var cornerLength = 2 * cornerRadius * Math.PI + 2;
  var totalLength = cornerLength * 4 + horizLineLength * 2 + vertLineLength * 2;

  // calc at what accumulated length each part of the rect starts
  var startT = cornerLength / 2;
  var startTCR = cornerLength / 2;
  var startTR = startTCR + horizLineLength;
  var startTRC = startTR + cornerLength / 2;
  var startRTC = startTRC + cornerLength / 2;
  var startBR = startRTC + vertLineLength;
  var startBCR = startBR + cornerLength / 2;
  var startBRC = startBCR + cornerLength / 2;
  var startBL = startBRC + horizLineLength;
  var startLCB = startBL + cornerLength / 2;
  var startLBC = startLCB + cornerLength / 2;
  var startTL = startLBC + vertLineLength;

  var d = 0, x = 0, y = 0, start = 0, end = 0, x1 = 0, y1 = 0, x2 = 0, y2 = 0;
  // percent expressed as a length-traveled-along-rect
  var accumLength = percent / 100 * totalLength;

  // clear the canvas
  context?.clearRect(0, 0, canvasWidth!, canvasHeight!);

  // top-left-start corner
  d = accumLength - startT;
  d = Math.min(d, cornerLength);
  if (d > 0) {
    x = offsetX + cornerRadius;
    y = offsetY + cornerRadius;
    start = 1.25 * Math.PI;
    end = 1.5 * Math.PI
    drawCorner(context, cornerRadius, x, y, start, end);
  }

  // top line
  d = accumLength - startTCR
  d = Math.min(d, horizLineLength);
  if (d > 0) {
    x1 = offsetX + cornerRadius;
    y1 = offsetY;
    x2 = offsetX + cornerRadius + d;
    y2 = offsetY;
    drawLine(context, x1, y1, x2, y2);
  }

  // top-right-1 corner
  d = accumLength - startTR;
  d = Math.min(d, cornerLength);
  if (d > 0) {
    x = offsetX + cornerRadius + horizLineLength - 2;
    y = offsetY + cornerRadius;
    start = 1.5 * Math.PI;
    end = 1.75 * Math.PI;
    drawCorner(context, cornerRadius, x, y, start, end);
  }

  // top-right-2 corner
  d = accumLength - startTRC;
  d = Math.min(d, cornerLength);
  if (d > 0) {
    x = offsetX + cornerRadius + horizLineLength - 2;
    y = offsetY + cornerRadius;
    start = 1.75 * Math.PI;
    end = 2 * Math.PI;
    drawCorner(context, cornerRadius, x, y, start, end);
  }

  // right line
  d = accumLength - startRTC;
  d = Math.min(d, vertLineLength);
  if (d > 0) {
    x1 = offsetX + cornerRadius + horizLineLength + cornerRadius - 2;
    y1 = offsetY + cornerRadius;
    x2 = offsetX + cornerRadius + horizLineLength + cornerRadius - 2;
    y2 = offsetY + cornerRadius + d;
    drawLine(context, x1, y1, x2, y2);
  }

  // bottom-right-1 corner
  d = accumLength - startBR;
  d = Math.min(d, cornerLength);
  if (d > 0) {
    x = offsetX + cornerRadius + horizLineLength - 2;
    y = offsetY + cornerRadius + vertLineLength - 2;
    start = 0;
    end = Math.PI / 4;
    drawCorner(context, cornerRadius, x, y, start, end);
  }

  // bottom-right-2 corner
  d = accumLength - startBCR;
  d = Math.min(d, cornerLength);
  if (d > 0) {
    x = offsetX + cornerRadius + horizLineLength - 2;
    y = offsetY + cornerRadius + vertLineLength - 2;
    start = Math.PI / 4;
    end = Math.PI / 2;
    drawCorner(context, cornerRadius, x, y, start, end);
  }

  // bottom line
  d = accumLength - startBRC;
  d = Math.min(d, horizLineLength);
  if (d > 0) {
    x1 = offsetX + cornerRadius + horizLineLength;
    y1 = offsetY + cornerRadius + vertLineLength + cornerRadius - 2;
    x2 = offsetX + cornerRadius + horizLineLength - d;
    y2 = offsetY + cornerRadius + vertLineLength + cornerRadius - 2;
    drawLine(context, x1, y1, x2, y2);
  }

  // bottom-left-1 corner
  d = accumLength - startBL;
  d = Math.min(d, cornerLength);
  if (d > 0) {
    x = offsetX + cornerRadius;
    y = offsetY + cornerRadius + vertLineLength - 2;
    start = Math.PI / 2;
    end = 3 * Math.PI / 4;
    drawCorner(context, cornerRadius, x, y, start, end);
  }

  // bottom-left-2 corner
  d = accumLength - startLCB;
  d = Math.min(d, cornerLength);
  if (d > 0) {
    x = offsetX + cornerRadius;
    y = offsetY + cornerRadius + vertLineLength - 2;
    start = 3 * Math.PI / 4;
    end = Math.PI;
    drawCorner(context, cornerRadius, x, y, start, end);
  }

  // left line
  d = accumLength - startLBC;
  d = Math.min(d, vertLineLength);
  if (d > 0) {
    x1 = offsetX;
    y1 = offsetY + cornerRadius + vertLineLength;
    x2 = offsetX;
    y2 = offsetY + cornerRadius + vertLineLength - d;
    drawLine(context, x1, y1, x2, y2);
  }

  // top-left-end corner
  d = accumLength - startTL;
  d = Math.min(d, cornerLength);
  if (d > 0) {
    x = offsetX + cornerRadius;
    y = offsetY + cornerRadius;
    start = Math.PI;
    // end = Math.PI + (d / cornerLength) * Math.PI / 2;
    end = 1.25 * Math.PI
    drawCorner(context, cornerRadius, x, y, start, end);
  }
}

function getArrowPosition(gradePosition: number, unitCompleted: boolean) {
  if (unitCompleted) {
    return 4
  }
  //
  return 2
}

function Card(props: Props) {
  const [isHovered, setIsHovered] = useState(false);
  const [isMovable, setIsMovable] = useState(false);
  const { 
    showLabel,
    type,
    unit,
    source,
    isDragable,
    draggableProvided,
    studyMode,
    target,
    handleMovableUnit,
    handleOpenUnit,
    handleRightArrowAction,
    handleLeftArrowAction,
    cardHovered,
    hoveredLessonId,
    index,
    handleCardHovered,
    handleSelectedCenterCard,
    programs,
  } = props
  const { addEventListenerForWindow, removeEventListenerForWindow } = useDragScrolling();

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [{ opacity, isDragging }, drag] = useDrag(
    () => ({
      type,
      item: () => {
        // like begin drag
        addEventListenerForWindow();
        return { unit }
      },
      canDrag: isDragable,
      end: () => {
        // like end drag
        removeEventListenerForWindow();
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
        opacity: !!monitor.isDragging() ? 0.4 : 1,
      }),
    }),
    [unit, type, isDragable],
  )

  const handleMoveLessonToCourse = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    handleLeftArrowAction?.(unit)
  }, [handleLeftArrowAction, unit])

  useEffect(() => {
    handleMovableUnit?.(isMovable)
  },[isMovable, handleMovableUnit])

  const actionContent = useMemo(() => {
    switch (source) {
      case 0: {
        return target === undefined ? null :
        <>
          <Tooltip position={'right'} tooltip={`Add Unit to ${target} Assessment`}>
            <div onMouseOver={() => setIsMovable(true)} onMouseLeave={() => setIsMovable(false)}>
              <IconMoveLesson onClick={handleMoveLessonToCourse} className={styles.arrowRightIcon}/>
            </div>
          </Tooltip>
        </>
      }
      default: {
        return <>
          <Tooltip position={'right'} tooltip={'Remove Unit'}>
            <IconRemovUnit onClick={() => handleRightArrowAction && handleRightArrowAction(unit)} className={styles.arrowLeftIcon}/>
          </Tooltip>
        </>
      }
    }
  }, [unit, source, target, handleRightArrowAction, handleMoveLessonToCourse]);

  const cardStyle = useMemo(() => {
    if (studyMode === MyQuizEnum.MyQuizzes || studyMode ===  'Student Introduction') {
      return styles.roundCard
    }
    return (isHovered && !cardHovered) ? styles.cardHovered : cardHovered ? styles.quickHoverCard : styles.card
  },[isHovered, studyMode, cardHovered])

  const gradeLabelStyle = useMemo(() => {
    const classes = [styles.labelContainer];
    
    classes.push(styles.tlLabel);
    
    return classes.join(' ');
  },[])

  const completeLabelStyle = useMemo(() => {
    const classes = [styles.titleContainer];
    classes.push(styles.trLabel);
    return classes.join(' ');
  },[])

  const iconViewStyle = useMemo(() => {
    const classes = [styles.iconWrapper];

    if (source === 0){
      const gradePos = 1
      const arrowPos = getArrowPosition(gradePos, showLabel ?? false)
      if (arrowPos === 2) {
        classes.push(styles.trArrow);
      } else if (arrowPos === 4) {
        classes.push(styles.brArrow);
      }
    } else {
      classes.push(styles.trArrow);
    }
    return classes.join(' ');
  },[source, showLabel])

  //Progress background
  var ctx = canvasRef.current?.getContext('2d');
  const cardElement = document.getElementById("imageCard")

  const cardRect = cardElement?.getBoundingClientRect();
  const canvasWidth = useMemo(() => {
    return cardRect && cardRect?.width - 1
  },[cardRect])
  const canvasHeight = useMemo(() => {
    return cardRect && cardRect?.height - 1
  },[cardRect])
  const borderWidth = 10

  useEffect(() => {
    // styling
    if (ctx) {
      ctx.lineWidth = borderWidth;
      ctx.strokeStyle = "#62C590";
    }
    drawPercentRect(ctx!, unit.progress ?? 0, borderWidth, canvasWidth!, canvasHeight!);

  }, [ctx, borderWidth, canvasWidth, canvasHeight, unit.progress])

  const handleClickLesson = useCallback(() => {
    if (!unit.isComingSoon) {
      handleOpenUnit?.(unit) 
    }
  }, [unit, handleOpenUnit])

  const handleClickLessonInfo = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if(handleCardHovered) {
      handleCardHovered(unit.id, true)
    }
    handleSelectedCenterCard?.(index % 3 !== 1)
  }, [handleCardHovered, handleSelectedCenterCard, index, unit])

  const gradeLabel = useMemo(() => {
    let gradeLabel = ''
    programs?.forEach(program => {
      const foundUnit = program.units?.find(item => item.id && item.id === unit.id)
      if (program.name !== "Your Completed Units" && foundUnit) {
        gradeLabel = program.name?.replace('Program', '') ?? ''
      }
    })
    return gradeLabel
  }, [programs, unit])
  const content = (
    <div className={styles.quickInfoView}>
      <div  className={styles.coverView}>
        <CoverVideoPlayer src={unit.popupCover} playing={true} metaData={{video_title: 'Cover Video'}} />
      </div>
      <div className={styles.overview}>
          <div className={styles.overviewSubView}>
            <div className={styles.subTitle}>{unit.name} - Lesson Overview</div>
            <OverviewForm unit={unit} />
          </div>
        </div>
    </div>
  );

  return (
    <Popover placement={(index % 3 === 1) ? "rightTop" : "bottom"} 
    title={''} content={content} open={studyMode === MyQuizEnum.QuizBuilder && cardHovered && hoveredLessonId === unit.id}>
    <div ref={drag} id={studyMode === MyQuizEnum.QuizBuilder ? unit.id : 'imageCard'} className={cardStyle} onClick={handleClickLesson} onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}
      style={{ backgroundImage: `url(${unit.coverImage ? getMediaAbsolutePath(unit.coverImage) : ''})`, opacity, cursor: isDragging ? 'grabbing' : isDragable ? 'grab' : unit.isComingSoon? 'default' : 'pointer',
      zIndex: (cardHovered && hoveredLessonId === unit.id) ? 4 : 0 }} {...draggableProvided?.dragHandleProps}>
      {showLabel && <div className={styles.progressBackBar}><div className={styles.progressBar} ></div></div>}
      <div className={styles.iconView}>
        <div className={iconViewStyle}>
          {actionContent}
        </div>
      </div>
      {(showLabel && studyMode !== MyQuizEnum.MyQuizzes && source === 0) && <div className={completeLabelStyle} style={{zIndex: isHovered ? 3 : 0}}>
        <IconMark />
      </div>}
      {(showLabel && gradeLabel && studyMode !== MyQuizEnum.MyQuizzes && source === 0) && <div className={gradeLabelStyle} style={{zIndex: isHovered ? 3 : 0}}>
        {gradeLabel}
      </div>}
      {(studyMode === MyQuizEnum.MyQuizzes && unit.progress) ? <MarkCheckIcon className={styles.completedBadge} width={34} height={34}/> : null}
      {(studyMode === MyQuizEnum.MyQuizzes && unit.progress) ? <InprogressIcon className={styles.inprogressBadge}/> : null}
      <div className={styles.infoIconView} onClick={handleClickLessonInfo}>
        <LordIcon src={IconInfo} isHover={true} colors={{primary: '#FFFFFF', secondary: '#FFFFFF'}} size={26}/>
      </div>
      {!!unit?.isNewCover && (
        <div className={styles.unitNameContainer}>
          {unit?.name || ''}
        </div>
      )}
    </div>
    </Popover>
  );
}

export default Card
